import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  sowingLifecycleApi,
  type CreateSowingRequest,
  type RecordGerminationWaveRequest,
  type AmendGerminationRequest,
  type RejectInstructionRequest,
  type AmendInstructionRequest,
  type DecideAmendmentRequest,
  type ChamberPlacementDto,
  type AcknowledgeStockEntryRequest,
  type MarkEntryReadyForDispatchRequest,
} from "../services/sowingLifecycleApi";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const sowingLifecycleKeys = {
  all: ["sowing-lifecycle"] as const,
  sowings: (instructionId: string) => [...sowingLifecycleKeys.all, "sowings", instructionId] as const,
  sowing: (sowingId: string) => [...sowingLifecycleKeys.all, "sowing", sowingId] as const,
  germinations: (sowingId: string) => [...sowingLifecycleKeys.all, "germinations", sowingId] as const,
  pendingAmendments: () => [...sowingLifecycleKeys.all, "pending-amendments"] as const,
  amendmentHistory: () => [...sowingLifecycleKeys.all, "amendment-history"] as const,
  availableGreenhouses: () => [...sowingLifecycleKeys.all, "available-greenhouses"] as const,
  availableBays: (greenhouseId: string) => [...sowingLifecycleKeys.all, "available-bays", greenhouseId] as const,
  availableBeds: (bayId: string) => [...sowingLifecycleKeys.all, "available-beds", bayId] as const,
  calendarEvents: (from: string, to: string) => [...sowingLifecycleKeys.all, "calendar-events", from, to] as const,
  plannerInstructions: (from: string, to: string) => [...sowingLifecycleKeys.all, "planner-instructions", from, to] as const,
  eligibleRequisitions: (instructionId: string) => [...sowingLifecycleKeys.all, "eligible-requisitions", instructionId] as const,
  stockEntriesReadyForGrowing: () => [...sowingLifecycleKeys.all, "stock-entries", "ready-for-growing"] as const,
  stockEntriesGrowing: () => [...sowingLifecycleKeys.all, "stock-entries", "growing"] as const,
  stockEntriesReadyForDispatch: () => [...sowingLifecycleKeys.all, "stock-entries", "ready-for-dispatch"] as const,
};

// ─── Sowing Queries ───────────────────────────────────────────────────────────

export function useSowingsByInstruction(instructionId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.sowings(instructionId),
    queryFn: () => sowingLifecycleApi.getSowingsByInstruction(instructionId),
    enabled: !!instructionId,
  });
}

export function useSowing(sowingId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.sowing(sowingId),
    queryFn: () => sowingLifecycleApi.getSowing(sowingId),
    enabled: !!sowingId,
  });
}

// ─── Germination Queries ──────────────────────────────────────────────────────

export function useGerminationsBySowing(sowingId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.germinations(sowingId),
    queryFn: () => sowingLifecycleApi.getGerminations(sowingId),
    enabled: !!sowingId,
  });
}

// ─── Amendment Queries ────────────────────────────────────────────────────────

export function usePendingAmendments() {
  return useQuery({
    queryKey: sowingLifecycleKeys.pendingAmendments(),
    queryFn: () => sowingLifecycleApi.getPendingAmendments(),
  });
}

export function useAmendmentHistory() {
  return useQuery({
    queryKey: sowingLifecycleKeys.amendmentHistory(),
    queryFn: () => sowingLifecycleApi.getAmendmentHistory(),
  });
}

// ─── Cascading Availability Queries ───────────────────────────────────────────

export function useAvailableGreenhouses() {
  return useQuery({
    queryKey: sowingLifecycleKeys.availableGreenhouses(),
    queryFn: () => sowingLifecycleApi.getAvailableGreenhouses(),
  });
}

export function useAvailableBays(greenhouseId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.availableBays(greenhouseId),
    queryFn: () => sowingLifecycleApi.getAvailableBays(greenhouseId),
    enabled: !!greenhouseId,
  });
}

export function useAvailableBeds(bayId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.availableBeds(bayId),
    queryFn: () => sowingLifecycleApi.getAvailableBeds(bayId),
    enabled: !!bayId,
  });
}

// ─── Calendar / Planning Queries ──────────────────────────────────────────────

export function useCalendarEvents(from: string, to: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.calendarEvents(from, to),
    queryFn: () => sowingLifecycleApi.getCalendarEvents(from, to),
    enabled: !!from && !!to,
  });
}

export function usePlannerInstructions(from: string, to: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.plannerInstructions(from, to),
    queryFn: () => sowingLifecycleApi.getPlannerInstructions(from, to),
    enabled: !!from && !!to,
  });
}

export function useEligibleRequisitions(instructionId: string) {
  return useQuery({
    queryKey: sowingLifecycleKeys.eligibleRequisitions(instructionId),
    queryFn: () => sowingLifecycleApi.getEligibleRequisitions(instructionId),
    enabled: !!instructionId,
  });
}

// ─── Sowing Mutations ─────────────────────────────────────────────────────────

export function useCreateSowing() {
  return useMutation({
    mutationFn: (payload: CreateSowingRequest) => sowingLifecycleApi.createSowing(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.sowings(variables.sowingInstructionId) });
      queryClient.invalidateQueries({ queryKey: ["sowing-instructions"] });
    },
  });
}

// ─── Germination Mutations ────────────────────────────────────────────────────

export function useAssignChamberPlacements() {
  return useMutation({
    mutationFn: ({ sowingId, placements }: { sowingId: string; placements: ChamberPlacementDto[] }) =>
      sowingLifecycleApi.assignChamberPlacements(sowingId, placements),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.sowing(variables.sowingId) });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.all });
    },
  });
}

export function useRecordGerminationWave() {
  return useMutation({
    mutationFn: (payload: RecordGerminationWaveRequest) => sowingLifecycleApi.recordGerminationWave(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.germinations(variables.sowingId) });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.sowing(variables.sowingId) });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.availableGreenhouses() });
      // Bed occupancy changed — next dialog needs fresh CurrentOccupancy values,
      // otherwise selecting a bed that was filled in this wave still shows the old count.
      queryClient.invalidateQueries({ queryKey: ["greenhouses"] });
      queryClient.invalidateQueries({ queryKey: [...sowingLifecycleKeys.all, "available-beds"] });
      // Stock entries and SI status change as side effects — refresh everything
      // downstream so the Growth / Ready for Dispatch / Greenhouse Stock surfaces
      // pick up the new wave without a manual refresh.
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesReadyForGrowing() });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesGrowing() });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesReadyForDispatch() });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
    },
  });
}

export function useFinalizeGermination() {
  return useMutation({
    mutationFn: (instructionId: string) => sowingLifecycleApi.finalizeGermination(instructionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
    },
  });
}

export function useCloseForSowing() {
  return useMutation({
    mutationFn: (instructionId: string) => sowingLifecycleApi.closeForSowing(instructionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
    },
  });
}

export function useAmendGermination() {
  return useMutation({
    mutationFn: (payload: AmendGerminationRequest) => sowingLifecycleApi.amendGermination(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.all });
      queryClient.invalidateQueries({ queryKey: ["greenhouses"] });
    },
  });
}

// ─── Rejection / Amendment Mutations ──────────────────────────────────────────

export function useRejectInstruction() {
  return useMutation({
    mutationFn: (payload: RejectInstructionRequest) => sowingLifecycleApi.rejectInstruction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowing-instructions"] });
    },
  });
}

export function useAmendInstruction() {
  return useMutation({
    mutationFn: (payload: AmendInstructionRequest) => sowingLifecycleApi.amendInstruction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowing-instructions"] });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.pendingAmendments() });
    },
  });
}

export function useDecideAmendment() {
  return useMutation({
    mutationFn: (payload: DecideAmendmentRequest) => sowingLifecycleApi.decideAmendment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sowing-instructions"] });
      queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.pendingAmendments() });
    },
  });
}

// ─── Stock Entry Pipeline (per-entry, multi-wave) ────────────────────────────

export function useStockEntriesReadyForGrowing() {
  return useQuery({
    queryKey: sowingLifecycleKeys.stockEntriesReadyForGrowing(),
    queryFn: () => sowingLifecycleApi.getStockEntriesReadyForGrowing(),
  });
}

export function useStockEntriesGrowing() {
  return useQuery({
    queryKey: sowingLifecycleKeys.stockEntriesGrowing(),
    queryFn: () => sowingLifecycleApi.getStockEntriesGrowing(),
  });
}

export function useStockEntriesReadyForDispatch() {
  return useQuery({
    queryKey: sowingLifecycleKeys.stockEntriesReadyForDispatch(),
    queryFn: () => sowingLifecycleApi.getStockEntriesReadyForDispatch(),
  });
}

export function useStockEntryPipelinePaged(params: {
  state: "growth" | "ready-for-growing" | "growing" | "ready-for-dispatch";
  pageNumber: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
}) {
  return useQuery({
    queryKey: [
      ...sowingLifecycleKeys.all,
      "stock-entries-paged",
      params.state,
      params.pageNumber,
      params.pageSize,
      params.search ?? "",
      params.sortBy ?? "",
    ],
    queryFn: () => sowingLifecycleApi.getStockEntryPipelinePaged(params),
    placeholderData: (prev) => prev,
  });
}

export function invalidateStockEntryPipeline() {
  queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesReadyForGrowing() });
  queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesGrowing() });
  queryClient.invalidateQueries({ queryKey: sowingLifecycleKeys.stockEntriesReadyForDispatch() });
  queryClient.invalidateQueries({ queryKey: [...sowingLifecycleKeys.all, "stock-entries-paged"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
  queryClient.invalidateQueries({ queryKey: ["sowing-instructions"] });
  // Bed CurrentOccupancy changes when stock entries are acknowledged/placed.
  queryClient.invalidateQueries({ queryKey: ["greenhouses"] });
  queryClient.invalidateQueries({ queryKey: [...sowingLifecycleKeys.all, "available-beds"] });
}

export function useAcknowledgeStockEntry() {
  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: AcknowledgeStockEntryRequest }) =>
      sowingLifecycleApi.acknowledgeStockEntry(entryId, payload),
    onSuccess: invalidateStockEntryPipeline,
  });
}

export function useMarkEntryReadyForDispatch() {
  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: MarkEntryReadyForDispatchRequest }) =>
      sowingLifecycleApi.markEntryReadyForDispatch(entryId, payload),
    onSuccess: invalidateStockEntryPipeline,
  });
}
